import { format } from 'date-fns'
import { Trash2, Star, CalendarRange, Layers } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { cn } from '@/utils/cn'
import type { UiCalendarLogicTemplateSummary } from '@/api/clt.adapter'

interface CltCardProps {
  template: UiCalendarLogicTemplateSummary
  onClick: () => void
  onDelete: () => void
  onSetDefault: () => void
}

export function CltCard({
  template,
  onClick,
  onDelete,
  onSetDefault,
}: CltCardProps) {
  const ruleCount = template.ruleCount
  const formattedLastEdit = template.updatedAt
    ? format(new Date(template.updatedAt), 'MMM d, yyyy')
    : 'N/A'

  const applyTargetCount = template.applyTargetCount
  const scopeSummary =
    applyTargetCount === 0
      ? 'No calendars'
      : `${applyTargetCount} calendar${applyTargetCount === 1 ? '' : 's'}`

  return (
    <Card
      className="cursor-pointer transition-shadow hover:shadow-md"
      onClick={onClick}
    >
      <CardHeader className="flex flex-row items-start justify-between gap-2 pb-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <CardTitle className="truncate text-base font-semibold">
              {template.name}
            </CardTitle>
            {template.isDefault && (
              <Badge className="gap-1 bg-primary/10 text-primary hover:bg-primary/10">
                <Star className="h-3 w-3 fill-current" />
                Default
              </Badge>
            )}
          </div>
          {template.description && (
            <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">
              {template.description}
            </p>
          )}
        </div>

        <div className="flex shrink-0 items-center">
          <Button
            variant="ghost"
            size="icon"
            title={template.isDefault ? 'This is the default' : 'Make default'}
            className={cn(
              'h-8 w-8 cursor-pointer',
              template.isDefault
                ? 'text-primary'
                : 'text-muted-foreground hover:text-primary'
            )}
            onClick={e => {
              e.stopPropagation()
              onSetDefault()
            }}
          >
            <Star
              className={cn('h-4 w-4', template.isDefault && 'fill-current')}
            />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 cursor-pointer text-muted-foreground hover:text-destructive"
            onClick={e => {
              e.stopPropagation()
              onDelete()
            }}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-3 pb-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="secondary" className="gap-1">
            <Layers className="h-3 w-3" />
            {ruleCount} rule{ruleCount === 1 ? '' : 's'}
          </Badge>
          <Badge variant="secondary" className="gap-1">
            <CalendarRange className="h-3 w-3" />
            {scopeSummary}
          </Badge>
        </div>

        <div className="flex items-center justify-end text-xs text-muted-foreground">
          <span>Last edit: {formattedLastEdit}</span>
        </div>
      </CardContent>
    </Card>
  )
}
